import type { FC } from 'react'
import Button from '@/components/Button/Button'
import useI18n from '@/hooks/use-i18n'
import type { FormData, FormErrors } from '@/hooks/useNewsletterForm'
import classNames from 'classnames'
import FormField, { FormFieldChangeEvent, FormFieldProps } from '@/components/FormField/FormField'

export type NewsletterFormField = 'firstName' | 'lastName' | 'email' | 'phone' | 'birthday' | 'location'

interface NewsletterFormFieldsProps {
  formData: FormData
  errors: FormErrors
  loading: boolean
  locations: LocationOption[]
  handleChange: (fieldName: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void
  handleDateChange: (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void
  className?: string
  inputClassName?: string
  selectClassName?: string
  rowClassName?: string
  formFieldClassName?: string
  submitButtonClassName?: string
  errorClassName?: string
  nameRowClassName?: string
}

export const NewsletterFormFields: FC<NewsletterFormFieldsProps> = ({
  formData,
  errors,
  loading,
  locations,
  handleChange,
  handleDateChange,
  inputClassName,
  selectClassName,
  rowClassName,
  formFieldClassName,
  submitButtonClassName,
  errorClassName,
  nameRowClassName,
}) => {
  const { i18n } = useI18n()

  // Define form fields configuration following ContactForm pattern
  const formFields: Record<NewsletterFormField, Partial<FormFieldProps>> = {
    firstName: {
      name: 'firstName',
      type: 'text',
      placeholder: i18n('firstName'),
      className: inputClassName,
      required: true,
      variant: 'minimal',
      label: i18n('firstName'),
    },
    lastName: {
      name: 'lastName',
      type: 'text',
      placeholder: i18n('lastName'),
      className: inputClassName,
      required: true,
      variant: 'minimal',
      label: i18n('lastName'),
    },
    email: {
      name: 'email',
      type: 'text',
      placeholder: i18n('emailAddress'),
      className: inputClassName,
      required: true,
      variant: 'minimal',
      label: i18n('emailAddress'),
      autoComplete: 'email',
    },
    phone: {
      name: 'phone',
      type: 'tel',
      placeholder: i18n('phoneNumber'),
      className: inputClassName,
      required: false,
      variant: 'minimal',
      label: i18n('phoneNumber'),
      autoComplete: 'tel',
    },
    birthday: {
      name: 'birthday',
      type: 'text',
      placeholder: `${i18n('birthday')} *`,
      className: inputClassName,
      required: true,
      variant: 'minimal',
      label: i18n('birthday'),
    },
    location: {
      name: 'location',
      type: 'select',
      placeholder: `${i18n('preferredLocation')} *`,
      className: selectClassName,
      required: true,
      variant: 'minimal',
      label: i18n('preferredLocation'),
      ariaLabel: i18n('preferredLocation'),
      items: locations.map(location =>
        location.isComingSoon ? `${location.label} (${i18n('comingSoon')})` : location.label,
      ),
    },
  }

  const getFormFieldProps = (fieldName: NewsletterFormField): FormFieldProps => {
    return {
      ...formFields[fieldName],
      value: formData[fieldName],
      onChange: handleChange(fieldName) as (e: FormFieldChangeEvent) => void,
      errorMessage: errors[fieldName],
    } as FormFieldProps
  }

  return (
    <>
      <div className={classNames(rowClassName, nameRowClassName)}>
        <FormField {...getFormFieldProps('firstName')} />
        <FormField {...getFormFieldProps('lastName')} />
      </div>

      <div className={rowClassName}>
        <div className={formFieldClassName}>
          <FormField {...getFormFieldProps('email')} />
        </div>
        <div className={formFieldClassName}>
          <FormField {...getFormFieldProps('phone')} />
        </div>
      </div>

      <div className={rowClassName}>
        <div className={formFieldClassName}>
          <label
            htmlFor="birthday"
            className="sr-only"
          >
            {i18n('birthday')}
          </label>
          <input
            id="birthday"
            type="text"
            pattern="(0[1-9]|1[0-2])/(0[1-9]|[12][0-9]|3[01])/[0-9]{4}"
            onFocus={e => {
              if (e.target.type === 'text') {
                e.target.type = 'text'
                e.target.placeholder = 'MM/DD/YYYY *'
              }
            }}
            onBlur={e => {
              if (!e.target.value) {
                e.target.type = 'text'
                e.target.placeholder = `${i18n('birthday')} *` || 'Birthday *'
              }
            }}
            name="birthday"
            value={formData.birthday}
            onChange={handleDateChange}
            className={inputClassName}
            required
            placeholder={`${i18n('birthday')} *` || 'Birthday *'}
            maxLength={10}
            inputMode="numeric"
          />
          {errors.birthday && <span className={errorClassName}>{errors.birthday}</span>}
        </div>
        <div className={formFieldClassName}>
          <FormField {...getFormFieldProps('location')} />
        </div>
      </div>

      <Button
        className={submitButtonClassName}
        type="submit"
        primary
        icon="caretRight"
        label={loading ? i18n('pleaseWait') : i18n('submit')}
        disabled={loading}
      />
    </>
  )
}
